import { useCallback } from 'react';
import { formatMealPayload } from '../../helpers';
import useDiarySearch from '../data/diary/useDiarySearch';
import useSaveMeal from '../data/meals/useSaveMeal';
import useUpdateMeal from '../data/meals/useUpdateMeal';

const useMealActions = ({ currentDay, entryType: mealType, closeWithDelay }) => {
    const {
        saveMeal,
        isSaving,
        isSuccess: isSaveSuccess,
        isError: isSaveError,
        error: saveError
    } = useSaveMeal(currentDay);

    const {
        updateMeal,
        isUpdating,
        isSuccess: isUpdateSuccess,
        isError: isUpdateError,
        error: updateError
    } = useUpdateMeal(currentDay);

    const { refresh } = useDiarySearch(mealType);

    // DERIVED STATE
    const isLoading = isSaving || isUpdating;
    const isSuccess = isSaveSuccess || isUpdateSuccess;
    const isError = isSaveError || isUpdateError;
    const error = saveError ?? updateError;

    const finalizeAction = useCallback(() => {
        refresh();
        closeWithDelay();
    }, [refresh, closeWithDelay]);

    const mutateMeal = useCallback(
        async ({ mode, mealId, mealData }) => {
            if (isLoading) return;

            if (mode !== 'create' && mode !== 'update') {
                throw new Error(`Invalid mutate mode: ${mode}`);
            }

            if (mode === 'update' && mealId == null) {
                throw new Error('Invariant violation: update without mealId');
            }

            const sanitized = {
                ...mealData,
                items: mealData.items.map(({ _isNew, unit, ...item }) => ({
                    ...item,
                    unit: unit?.id ?? 0
                }))
            };

            const formatted = formatMealPayload(sanitized, mealType, mode, mealId);

            if (!formatted) return;

            if (mode === 'create') await saveMeal(formatted);
            else await updateMeal(mealId, formatted);

            finalizeAction();
        },
        [isLoading, mealType, saveMeal, updateMeal, finalizeAction]
    );

    return {
        mutateMeal,
        isLoading,
        isSuccess,
        isError,
        error
    };
};

export default useMealActions;
